import React, { createContext, useContext, ReactNode, useMemo, useState, useEffect } from 'react';
import { useLocationDataFetching } from '@/hooks/useLocationDataFetching';
import { LocationByRadius } from '@/components/business/onboarding/types/locationTypes';
import { useFirebase } from './FirebaseProvider'; // To get business address
import { getCoordinatesFromAddress } from '@/utils/googleMapsUtils'; // Geocoding utility
import { Business } from '@/schemas/business'; // Import Business type

// Define the context type
interface LocationContextType {
  masterLocations: LocationByRadius[];
  isLoadingLocations: boolean;
  locationsError: Error | null;
  
  fetchedBusinessCoordinates: { latitude: number; longitude: number } | null;
  isGeocoding: boolean;
  geocodingError: string | null;
  
  // This context will also need to know the current business and targetType
  // For now, assuming currentBusiness is passed as a prop or obtained via useFirebase
  // And targetType is also made available (e.g. from GoCampaignContext or props)
}

// Create context with default values
const LocationContext = createContext<LocationContextType | undefined>(undefined);

// Hook for using the context
export const useLocationContext = () => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocationContext must be used within a LocationContextProvider');
  }
  return context;
};

// Provider props
interface LocationContextProviderProps {
  children: ReactNode;
  currentBusiness: Business | null; // The specific business for this context
  activeTargetType?: 'radius' | 'state' | 'all_us' | null; 
}

/**
 * Provider component for location context.
 * Loads master location data and handles geocoding for business address.
 */
export const LocationContextProvider: React.FC<LocationContextProviderProps> = ({
  children,
  currentBusiness, // Use the passed-in currentBusiness
  activeTargetType 
}) => {
  const { 
    locations: masterLocationsData, 
    isLoading: isLoadingMasterLocations, 
    error: masterLocationsError 
  } = useLocationDataFetching();

  // const { selectedBusiness } = useFirebase(); // Not using this for specific business address

  const [fetchedBusinessCoordinates, setFetchedBusinessCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingError, setGeocodingError] = useState<string | null>(null);

  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;

  useEffect(() => {
    const geocodeAddress = async () => {
      if (activeTargetType === 'radius' && currentBusiness?.address && googleMapsApiKey) {
        setIsGeocoding(true);
        setGeocodingError(null);
        setFetchedBusinessCoordinates(null); // Clear previous coordinates
        try {
          // Construct address string carefully, ensuring parts are defined
          let addressParts = [currentBusiness.address];
          if (currentBusiness.city) addressParts.push(currentBusiness.city);
          if (currentBusiness.state) addressParts.push(currentBusiness.state);
          if (currentBusiness.zip) addressParts.push(currentBusiness.zip);
          const fullAddress = addressParts.join(', ').trim();
          
          if (!fullAddress) {
            setGeocodingError('Business address is incomplete for geocoding.');
            setIsGeocoding(false);
            return;
          }

          const coords = await getCoordinatesFromAddress(fullAddress, googleMapsApiKey);
          setFetchedBusinessCoordinates(coords);
          if (!coords) {
            setGeocodingError('Could not geocode business address.');
          }
        } catch (error) {
          console.error("Error geocoding address:", error);
          setGeocodingError('Failed to geocode business address.');
        } finally {
          setIsGeocoding(false);
        }
      } else {
        // Clear coordinates if not in radius mode or no address/business
        setFetchedBusinessCoordinates(null);
        setIsGeocoding(false);
        setGeocodingError(null);
      }
    };

    geocodeAddress();
  }, [currentBusiness, activeTargetType, googleMapsApiKey]); // Depend on the whole currentBusiness object

  const contextValue = useMemo(() => ({
    masterLocations: masterLocationsData,
    isLoadingLocations: isLoadingMasterLocations,
    locationsError: masterLocationsError,
    fetchedBusinessCoordinates,
    isGeocoding,
    geocodingError,
  }), [
    masterLocationsData, 
    isLoadingMasterLocations, 
    masterLocationsError,
    fetchedBusinessCoordinates,
    isGeocoding,
    geocodingError,
  ]);
  
  return (
    <LocationContext.Provider value={contextValue}>
      {children}
    </LocationContext.Provider>
  );
};
